import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { downloadXlsx } from '../utils/exportXlsx';
import ReportShell from './ReportShell';

// 811 billing report — tickets each city answered in WA 811 (= completed) in a date
// range, who responded (PM1, typed by the team in ArcGIS), and the weekly volume.
// Completion = the city's first real response in the ticket's Status History.

// Fixed city → colour (never re-ordered by the filter). Validated as a 3-slot
// categorical set on the app's #0d1b2e surface (all checks pass, incl. all-pairs CVD).
const CITIES = [
  { key: 'STEILACOOM', label: 'Steilacoom', color: '#3987e5' },
  { key: 'FIFE', label: 'Fife', color: '#d95926' },
  { key: 'ALGONA', label: 'Algona', color: '#199e70' },
];
const SURFACE = '#0d1b2e';
const BORDER = '#1e3a5f';
const inputStyle = { backgroundColor: SURFACE, borderColor: BORDER };

// Dates are Pacific calendar days as YYYY-MM-DD strings; do the maths in UTC so the
// browser's own timezone can't shift a day.
const iso = (d) => d.toISOString().slice(0, 10);
const parseDay = (s) => new Date(`${s}T00:00:00Z`);
const addDays = (s, n) => iso(new Date(parseDay(s).getTime() + n * 86400000));
const mondayOf = (s) => { const d = parseDay(s); return addDays(s, -((d.getUTCDay() + 6) % 7)); };
const fmtDay = (s) => { if (!s) return '—'; const [y, m, d] = s.split('-'); return `${+m}/${+d}/${y}`; };
const shortDay = (s) => { const [, m, d] = s.split('-'); return `${+m}/${+d}`; };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' }) : '—');
const todayPacific = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

function presets() {
  const today = todayPacific();
  const [y, m] = today.split('-').map(Number);
  const firstThis = `${y}-${String(m).padStart(2, '0')}-01`;
  const lastPrev = addDays(firstThis, -1);
  return {
    'This month': [firstThis, today],
    'Last month': [`${lastPrev.slice(0, 7)}-01`, lastPrev],
    'Last 12 weeks': [addDays(mondayOf(today), -77), today],
  };
}

export default function TicketReport({ onClose, embedded = false }) {
  const init = presets()['This month'];
  const [from, setFrom] = useState(init[0]);
  const [to, setTo] = useState(init[1]);
  const [city, setCity] = useState('all');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function run(f = from, t = to, c = city) {
    if (!f || !t || f > t) { setError('Pick a start date on or before the end date.'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/tickets/report', { params: { from: f, to: t, city: c } });
      setReport(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not build the report.');
    } finally {
      setLoading(false);
    }
  }

  // Run once on open with the default range.
  useEffect(() => { run(); }, []);

  const shownCities = city === 'all' ? CITIES : CITIES.filter((c) => c.key === city);
  const rows = report?.rows || [];

  const byCity = useMemo(() => {
    const m = Object.fromEntries(CITIES.map((c) => [c.key, 0]));
    rows.forEach((r) => { m[r.city] = (m[r.city] || 0) + 1; });
    return m;
  }, [rows]);

  // Every Monday from the range's first week to its last, zero-filled.
  const weeks = useMemo(() => {
    if (!report) return [];
    const out = [];
    for (let w = mondayOf(report.from); w <= report.to; w = addDays(w, 7)) {
      out.push({ week: w, counts: Object.fromEntries(CITIES.map((c) => [c.key, 0])), total: 0 });
    }
    const idx = Object.fromEntries(out.map((w, i) => [w.week, i]));
    rows.forEach((r) => {
      const w = out[idx[r.week_start]];
      if (w) { w.counts[r.city] = (w.counts[r.city] || 0) + 1; w.total++; }
    });
    return out;
  }, [report, rows]);

  const byPm = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const k = (r.pm1 || '').trim() || '(not filled in)';
      m[k] = m[k] || Object.fromEntries(CITIES.map((c) => [c.key, 0]));
      m[k][r.city] = (m[k][r.city] || 0) + 1;
    });
    return Object.entries(m).sort((a, b) => sum(b[1]) - sum(a[1]));
  }, [rows]);

  async function exportExcel() {
    await downloadXlsx({
      filename: `811-billing-${report.from}-to-${report.to}${city !== 'all' ? `-${city.toLowerCase()}` : ''}.xlsx`,
      sheetName: '811 Billing',
      headers: ['Completed', 'Week of', 'Ticket #', 'City (Locate For)', 'Ticket Place', 'House #', 'Street', 'Intersecting Street', 'Called In', '811 Status', 'Responded By (PM1)', 'Progress', 'Contractor'],
      rows: rows.map((r) => [
        fmtDay(r.completed_day), fmtDay(r.week_start), r.ticket_no, r.city, r.place || '', r.address || '', r.street || '', r.cross_street || '',
        fmtDate(r.call_date), r.city_status || '', r.pm1 || '', r.progress || '', r.excavator || '',
      ]),
    });
  }

  return (
    <ReportShell embedded={embedded} onClose={onClose} title="811 Billing Report" maxWidth="max-w-5xl"
      subtitle="Tickets completed in the date range — completed = the city's first response in WA 811">
        {/* Controls */}
        <div className="flex items-center gap-3 px-5 pt-4 pb-3 flex-wrap">
          <label className="text-xs text-gray-400 uppercase tracking-wide">From</label>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="text-sm rounded px-2 py-1.5 border text-white focus:outline-none [color-scheme:dark]" style={inputStyle} />
          <label className="text-xs text-gray-400 uppercase tracking-wide">To</label>
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="text-sm rounded px-2 py-1.5 border text-white focus:outline-none [color-scheme:dark]" style={inputStyle} />
          <select value={city} onChange={(e) => setCity(e.target.value)} className="text-sm rounded px-2 py-1.5 border text-white focus:outline-none" style={inputStyle}>
            <option value="all">All cities</option>
            {CITIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <button
            onClick={() => run()}
            disabled={loading}
            className="text-xs px-3 py-2 rounded font-bold uppercase tracking-wide transition disabled:opacity-50"
            style={{ backgroundColor: '#c9a84c', color: SURFACE }}
          >
            {loading ? 'Running…' : 'Run report'}
          </button>
          <button
            onClick={exportExcel}
            disabled={!rows.length}
            className="text-xs px-3 py-2 rounded border font-bold uppercase tracking-wide text-gray-300 hover:text-white transition disabled:opacity-40"
            style={{ borderColor: BORDER }}
          >
            Export to Excel
          </button>
          <div className="flex gap-1 ml-auto">
            {Object.entries(presets()).map(([name, [f, t]]) => (
              <button
                key={name}
                onClick={() => { setFrom(f); setTo(t); run(f, t); }}
                className="text-[11px] px-2 py-1 rounded border text-gray-400 hover:text-white transition"
                style={{ borderColor: BORDER }}
              >
                {name}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-y-auto px-5 pb-5 space-y-5">
          {error && <p className="text-red-400 text-sm">{error}</p>}
          {report?.layerError && <p className="text-yellow-300 text-xs">⚠ {report.layerError}</p>}

          {report && (
            <>
              {/* Totals */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label={`Completed ${fmtDay(report.from)} – ${fmtDay(report.to)}`} value={rows.length} />
                {shownCities.map((c) => <Stat key={c.key} label={c.label} value={byCity[c.key] || 0} color={c.color} />)}
              </div>

              {/* Weekly volume */}
              <section>
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                  <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wide">Completed per week <span className="normal-case font-normal text-gray-500">· weeks start Monday</span></h3>
                  {shownCities.length > 1 && (
                    <div className="flex gap-4 text-xs text-gray-300">
                      {shownCities.map((c) => (
                        <span key={c.key} className="flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c.color }} />
                          {c.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <WeeklyChart weeks={weeks} cities={shownCities} />
              </section>

              {/* Who responded */}
              {rows.length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2">Responded by (PM1)</h3>
                  <table className="text-xs">
                    <thead>
                      <tr className="text-gray-500">
                        <th className="text-left font-semibold pr-6 pb-1">PM1</th>
                        {shownCities.map((c) => <th key={c.key} className="text-right font-semibold px-3 pb-1">{c.label}</th>)}
                        <th className="text-right font-semibold pl-3 pb-1">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byPm.map(([pm, counts]) => (
                        <tr key={pm} className="border-t" style={{ borderColor: BORDER }}>
                          <td className={`pr-6 py-1 ${pm.startsWith('(') ? 'text-gray-500 italic' : 'text-white'}`}>{pm}</td>
                          {shownCities.map((c) => <td key={c.key} className="text-right px-3 py-1 text-gray-300 tabular-nums">{counts[c.key] || 0}</td>)}
                          <td className="text-right pl-3 py-1 text-white font-semibold tabular-nums">{sum(counts)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {/* Tickets */}
              <section>
                <h3 className="text-xs font-semibold text-gray-300 uppercase tracking-wide mb-2">
                  Tickets ({rows.length}){report.openNow > 0 && <span className="normal-case font-normal text-gray-500"> · {report.openNow} open ticket{report.openNow === 1 ? '' : 's'} still waiting on the city right now</span>}
                </h3>
                {rows.length === 0 ? (
                  <p className="text-gray-500 text-sm">No tickets were completed in this range.</p>
                ) : (
                  <div className="rounded-lg border overflow-x-auto" style={{ borderColor: BORDER }}>
                    <table className="w-full text-xs">
                      <thead style={{ backgroundColor: '#080f1a' }}>
                        <tr>
                          {['Completed', 'Ticket #', 'City', 'Address', 'Called In', '811 Status', 'Responded By (PM1)', 'Progress', 'Contractor'].map((h) => (
                            <th key={h} className="text-left px-3 py-2 font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y" style={{ borderColor: BORDER }}>
                        {rows.map((r) => (
                          <tr key={r.ticket_no} className="hover:bg-white/5">
                            <td className="px-3 py-2 text-gray-300 whitespace-nowrap">{fmtDay(r.completed_day)}</td>
                            <td className="px-3 py-2 font-bold whitespace-nowrap" style={{ color: '#c9a84c' }}>{r.ticket_no}</td>
                            <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                              <span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ backgroundColor: CITIES.find((c) => c.key === r.city)?.color }} />
                              {r.city}{r.place && r.place !== r.city && <span className="text-gray-500"> (in {r.place})</span>}
                            </td>
                            <td className="px-3 py-2 text-gray-300">{[r.address, r.street].filter(Boolean).join(' ') || '—'}{r.cross_street && <span className="text-gray-500"> @ {r.cross_street}</span>}</td>
                            <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{fmtDate(r.call_date)}</td>
                            <td className="px-3 py-2 text-gray-300 max-w-[14rem]">{r.city_status || '—'}</td>
                            <td className={`px-3 py-2 ${r.pm1 ? 'text-white' : 'text-gray-600'}`}>{r.pm1 || '—'}</td>
                            <td className="px-3 py-2 text-gray-400">{r.progress || '—'}</td>
                            <td className="px-3 py-2 text-gray-400">{r.excavator || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
    </ReportShell>
  );
}

const sum = (o) => Object.values(o).reduce((a, b) => a + b, 0);

function Stat({ label, value, color }) {
  return (
    <div className="rounded-lg border px-3 py-2" style={{ borderColor: BORDER, backgroundColor: '#080f1a' }}>
      <div className="flex items-center gap-1.5 text-[11px] text-gray-400 uppercase tracking-wide">
        {color && <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: color }} />}
        {label}
      </div>
      <div className="text-2xl font-black text-white tabular-nums">{value}</div>
    </div>
  );
}

// Stacked weekly columns, one segment per city. Bars ≤24px, 2px surface gap between
// segments, 4px rounded top on the top segment only (square at the baseline),
// recessive grid, week totals above each bar, per-week hover tooltip.
function WeeklyChart({ weeks, cities }) {
  const [hover, setHover] = useState(null);
  if (!weeks.length) return null;

  const W = 900, H = 240;
  const pad = { top: 22, right: 8, bottom: 20, left: 34 };
  const iw = W - pad.left - pad.right;
  const ih = H - pad.top - pad.bottom;
  const max = Math.max(1, ...weeks.map((w) => cities.reduce((a, c) => a + w.counts[c.key], 0)));
  const step = niceStep(max);
  const top = Math.ceil(max / step) * step;
  const y = (v) => pad.top + ih - (v / top) * ih;
  const band = iw / weeks.length;
  const bw = Math.min(24, band * 0.6);
  const labelEvery = Math.ceil(weeks.length / 14);
  const ticks = [];
  for (let v = 0; v <= top; v += step) ticks.push(v);

  const hw = hover != null ? weeks[hover] : null;
  const hoverTotal = hw ? cities.reduce((a, c) => a + hw.counts[c.key], 0) : 0;

  return (
    <div className="relative rounded-lg border p-2" style={{ borderColor: BORDER }}>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img"
        aria-label={`Tickets completed per week: ${weeks.map((w) => `week of ${shortDay(w.week)} ${cities.reduce((a, c) => a + w.counts[c.key], 0)}`).join(', ')}`}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={pad.left} x2={W - pad.right} y1={y(v)} y2={y(v)} stroke={v === 0 ? '#3b5475' : '#1a2d45'} strokeWidth="1" />
            <text x={pad.left - 6} y={y(v) + 3.5} textAnchor="end" fontSize="10" fill="#6b7a90">{v}</text>
          </g>
        ))}
        {weeks.map((w, i) => {
          const cx = pad.left + band * i + band / 2;
          const x = cx - bw / 2;
          let acc = 0;
          const segs = cities.filter((c) => w.counts[c.key] > 0);
          const total = segs.reduce((a, c) => a + w.counts[c.key], 0);
          return (
            <g key={w.week}>
              {hover === i && <rect x={pad.left + band * i} y={pad.top} width={band} height={ih} fill="#ffffff" opacity="0.04" />}
              {segs.map((c, si) => {
                const v = w.counts[c.key];
                const y1 = y(acc + v);
                const y0 = y(acc);
                acc += v;
                const isTop = si === segs.length - 1;
                const h = Math.max(0, y0 - y1 - (si > 0 ? 2 : 0)); // 2px surface gap below each upper segment
                const yTop = y1;
                return isTop
                  ? <path key={c.key} d={roundedTop(x, yTop, bw, h, Math.min(4, h))} fill={c.color} />
                  : <rect key={c.key} x={x} y={yTop} width={bw} height={h} fill={c.color} />;
              })}
              {total > 0 && weeks.length <= 26 && (
                <text x={cx} y={y(total) - 5} textAnchor="middle" fontSize="10" fontWeight="600" fill="#cbd5e1">{total}</text>
              )}
              {i % labelEvery === 0 && (
                <text x={cx} y={H - pad.bottom + 15} textAnchor="middle" fontSize="10" fill="#6b7a90">{shortDay(w.week)}</text>
              )}
              {/* hit target: the whole week band, taller than the bar */}
              <rect x={pad.left + band * i} y={pad.top} width={band} height={ih} fill="transparent"
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
            </g>
          );
        })}
      </svg>

      {hw && (
        <div
          className="pointer-events-none absolute top-2 text-xs rounded border px-3 py-2 shadow-lg"
          style={{
            backgroundColor: '#080f1a', borderColor: BORDER,
            left: `${Math.min(78, Math.max(2, ((pad.left + band * hover + band / 2) / W) * 100 - 8))}%`,
          }}
        >
          <div className="text-gray-400 mb-1">Week of {fmtDay(hw.week)}</div>
          {cities.map((c) => (
            <div key={c.key} className="flex items-center gap-2 justify-between">
              <span className="flex items-center gap-1.5 text-gray-300">
                <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: c.color }} />{c.label}
              </span>
              <span className="text-white tabular-nums">{hw.counts[c.key]}</span>
            </div>
          ))}
          {cities.length > 1 && (
            <div className="flex justify-between border-t mt-1 pt-1 text-white font-semibold" style={{ borderColor: BORDER }}>
              <span>Total</span><span className="tabular-nums">{hoverTotal}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function niceStep(max) {
  const raw = max / 4;
  const mag = 10 ** Math.floor(Math.log10(raw || 1));
  const n = raw / mag;
  return Math.max(1, (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag);
}

// Rect with only the top two corners rounded (data end), square at the bottom.
function roundedTop(x, y, w, h, r) {
  if (h <= 0) return '';
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
}
